import { Add, DeleteOutlined } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import React from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Configuration,
  CustomFieldDefinition,
  ExtendedFieldError,
  FieldDefinition,
  ITEM_NAMES,
  PROJECT_NAMES,
} from "vmd-types";
import { checkField, VIEW_NAMES } from "vmd-utils";

import {
  CATEGORY_OPTION_DEFAULT_COLOR,
  SUCCESS_COLORS,
  TOGGLE_GREEN,
} from "../../constants/colors";
import { AntSwitch } from "../components/ant-switch";
import { CategoryFields } from "../custom-fields/category-field";
import { CustomizableFormField } from "../custom-fields/customizable-form-field";
import { DefaultFields } from "../custom-fields/default-field";
import { FixedEnumValuesPreview } from "../custom-fields/fixed-enum-values-preview";
import { SemaphoreFields } from "../custom-fields/semaphore-fields";
import { StatusFields } from "../custom-fields/status-fields";
import { FormBlocksSection } from "./form-blocks/form-blocks-section";

export interface ReportingFieldsFormProps {
  configuration?: Configuration;
  language?: string;
  setUpdatedFieldsFlag: (change: "FieldActivated" | "FieldDeactivated") => void;
}

export const ReportingFieldsForm: PimoReactComponent<
  ReportingFieldsFormProps,
  "define:reporting-data",
  Configuration
> = ({ language = "en", setUpdatedFieldsFlag }) => {
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton?: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });

  const itemName =
    ITEM_NAMES.find(
      (entry) =>
        entry.single === (formState.configuration?.itemName ?? "Tracking Item")
    ) ?? ITEM_NAMES[0];
  const projectName =
    PROJECT_NAMES.find(
      (entry) =>
        entry.single === (formState.configuration?.projectName ?? "Project")
    ) ?? PROJECT_NAMES[0];

  const [isAccordionExpanded, setIsAccordionExpanded] = React.useState<
    string | false
  >(false);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, newExpanded: boolean) => {
      setIsAccordionExpanded(newExpanded ? panel : false);
    };

  const handleAppend = (type: "itemReporting" | "projectReporting") => {
    const newItem: CustomFieldDefinition = {
      id: uuidv4(),
      label: "",
      type: "Text",
      purpose: type,
      options: [{ name: "", color: CATEGORY_OPTION_DEFAULT_COLOR }],
      enabled: true,
      mandatory: false,
      flag: "create",
    };

    setValue("configuration.customFields", [
      ...(formState?.configuration.customFields ?? []),
      newItem,
    ]);
    setValue("enableSaveButton", true);
    setUpdatedFieldsFlag("FieldActivated");
    const element = document.getElementById("dummy-to-scroll-into");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "auto",
      }}
    >
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 500,
        }}
      >
        {getLocalizedMessage(language, "reportingData.title") ||
          "Reporting Data"}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: grey[600],
        }}
      >
        {getLocalizedMessage(language, "reportingData.description") ||
          `Reporting data is the data that is regularly updated by the reporting owners. Please select the data fields for the ${projectName.plural} and the ${itemName.plural}.`}
      </Typography>
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 500,
          textAlign: "left",

          width: "100%",
        }}
      >
        {getLocalizedMessage(language, "reportingData.projectLevel") ||
          `Reporting Data on ${projectName.single} Level`}
      </Typography>
      <Paper
        sx={{
          borderRadius: "12px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        {" "}
        <Grid2
          size={12}
          container
          alignItems="center"
          sx={{ height: 65, p: 2, width: "98%" }}
        >
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataField") || "Data Field"}
          </Grid2>
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataType") || "Data Type"}
          </Grid2>
          <Grid2 size={3}></Grid2>
          <Grid2 size={1.8}></Grid2>
          <Grid2 size={1.2}>
            <Button
              variant="outlined"
              onClick={() => handleAppend("projectReporting")}
              startIcon={<Add />}
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                padding: "7px 16px",
                whiteSpace: "nowrap",
                minWidth: "fit-content",
              }}
            >
              {(
                getLocalizedMessage(language, "addItem") ||
                `Add ${itemName.single}`
              ).replace("{{itemName}}", itemName.single)}
            </Button>
          </Grid2>
        </Grid2>
        {formState.configuration?.fieldsDefinition
          ?.filter((f) => f.purpose === "projectReporting")
          ?.map((field) => (
            <Accordion
              expanded={false}
              onChange={() => {}}
              elevation={0}
              key={field.fieldName + field.purpose}
              disableGutters
            >
              <AccordionSummary
                aria-controls="panel1-content"
                id="panel1-header"
                expandIcon={null}
                sx={{
                  "& .MuiAccordionSummary-content": {
                    marginLeft: 0,
                  },
                  cursor: "default",
                  userSelect: "text",
                  width: "98%",
                  height: 65,
                }}
              >
                <Grid2
                  size={12}
                  container
                  alignItems="center"
                  sx={{ height: 65 }}
                >
                  <Grid2 size={3} sx={{ fontSize: "14px" }}>
                    {field.fieldName === "spentBudget"
                      ? `${field.label} (€)`
                      : field.label}
                  </Grid2>
                  <Grid2 size={3} sx={{ fontSize: "14px" }}>
                    {field.type}
                  </Grid2>
                  <Grid2 size={3}>
                    <FormControlLabel
                      labelPlacement="start"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                        },
                      }}
                      control={
                        <Checkbox
                          onClick={(e) => {
                            // stop the click from bubbling to AccordionSummary
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const currentField = {
                              ...field,
                              isMandatory: e.target.checked,
                            };

                            const newFieldsDefinition =
                              formState.configuration?.fieldsDefinition ?? [];
                            const fullArrayIndex =
                              newFieldsDefinition.findIndex(
                                (f) =>
                                  f.fieldName === field.fieldName &&
                                  f.purpose === field.purpose
                              );

                            if (fullArrayIndex !== -1) {
                              newFieldsDefinition[fullArrayIndex] =
                                currentField;
                              setValue(
                                `configuration.fieldsDefinition`,
                                newFieldsDefinition
                              );
                            }
                            if (formState.enableSaveButton !== true) {
                              setValue("enableSaveButton", true);
                            }
                          }}
                          icon={
                            <span
                              style={{
                                width: 20,
                                height: 20,
                                border: "2px solid",
                                borderColor: grey[700],

                                display: "inline-block",
                                backgroundColor: "#fff",
                              }}
                            />
                          }
                          checkedIcon={
                            <span
                              style={{
                                width: 20,
                                height: 20,

                                border: "2px solid",
                                borderColor: grey[700],
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#fff",
                              }}
                            >
                              <CheckIcon
                                style={{
                                  fontSize: 18,
                                  color: grey[700],
                                }}
                              />
                            </span>
                          }
                          checked={field.isMandatory}
                        />
                      }
                      label={
                        getLocalizedMessage(language, "mandatory") ||
                        "Mandatory"
                      }
                    />
                  </Grid2>
                  <Grid2 size={3} sx={{ fontSize: "14px" }}>
                    <FormControlLabel
                      control={
                        <AntSwitch
                          checked={field.enabled}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const currentField = {
                              ...field,
                              enabled: e.target.checked,
                            };

                            const newFieldsDefinition = [
                              ...(formState.configuration?.fieldsDefinition ??
                                []),
                            ];
                            // Find the correct index in the full array
                            const fullArrayIndex =
                              newFieldsDefinition.findIndex(
                                (f) =>
                                  f.fieldName === field.fieldName &&
                                  f.purpose === field.purpose
                              );

                            if (fullArrayIndex !== -1) {
                              newFieldsDefinition[fullArrayIndex] =
                                currentField;
                              setValue(
                                `configuration.fieldsDefinition`,
                                newFieldsDefinition
                              );
                            }
                            setValue("enableSaveButton", true);
                            setUpdatedFieldsFlag(
                              e.target.checked
                                ? "FieldActivated"
                                : "FieldDeactivated"
                            );
                          }}
                        />
                      }
                      label={
                        getLocalizedMessage(language, "activate") || "Activate"
                      }
                      labelPlacement="start"
                      sx={{
                        "& .MuiSwitch-thumb": {
                          backgroundColor: grey[300],
                        },
                        "& .MuiSwitch-track": {
                          color: TOGGLE_GREEN,
                        },
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                        },
                      }}
                    />
                  </Grid2>
                </Grid2>
              </AccordionSummary>
            </Accordion>
          ))}
        {formState.configuration?.customFields
          ?.filter((f) => f.flag !== "delete")
          ?.map((field, index) => {
            if (field.purpose !== "projectReporting") return null;
            return (
              <Accordion
                expanded={isAccordionExpanded === field.id.toString()}
                onChange={handleChange(field.id.toString() as string)}
                elevation={0}
                key={field.id}
                disableGutters
              >
                <AccordionSummary
                  aria-controls="panel1-content"
                  id="panel1-header"
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      marginLeft: 0,
                    },
                    cursor: "default",
                    userSelect: "text",
                    width: "100%",
                    height: 65,
                  }}
                >
                  <Grid2
                    size={12}
                    container
                    alignItems="center"
                    sx={{ height: 65 }}
                  >
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {field.label}
                    </Grid2>
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {field.type}
                    </Grid2>
                    <Grid2 size={3}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                        control={
                          <Checkbox
                            onClick={(e) => {
                              // stop the click from bubbling to AccordionSummary
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                mandatory: e.target.checked,
                              };

                              const newFieldsDefinition =
                                formState.configuration?.customFields ?? [];
                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) => f.id === field.id
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  `configuration.customFields`,
                                  newFieldsDefinition
                                );
                              }
                              if (formState.enableSaveButton !== true) {
                                setValue("enableSaveButton", true);
                              }
                            }}
                            icon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  border: "2px solid",
                                  borderColor: grey[700],

                                  display: "inline-block",
                                  backgroundColor: "#fff",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,

                                  border: "2px solid",
                                  borderColor: grey[700],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <CheckIcon
                                  style={{
                                    fontSize: 18,
                                    color: grey[700],
                                  }}
                                />
                              </span>
                            }
                            checked={field.mandatory}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "mandatory") ||
                          "Mandatory"
                        }
                      />
                    </Grid2>
                    <Grid2 size={2} sx={{ fontSize: "14px" }}>
                      <FormControlLabel
                        control={
                          <AntSwitch
                            checked={field.enabled}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                enabled: e.target.checked,
                              };

                              const newFieldsDefinition = [
                                ...(formState.configuration?.customFields ??
                                  []),
                              ];

                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) => f.id === field.id
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  `configuration.fieldsDefinition`,
                                  newFieldsDefinition
                                );
                              }
                              if (formState.enableSaveButton !== true) {
                                setValue("enableSaveButton", true);
                              }
                            }}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "activate") ||
                          "Activate"
                        }
                        labelPlacement="start"
                        sx={{
                          "& .MuiSwitch-thumb": {
                            backgroundColor: grey[300],
                          },
                          "& .MuiSwitch-track": {
                            color: TOGGLE_GREEN,
                          },
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                      />
                    </Grid2>
                    <Grid2 size={1} sx={{ fontSize: "14px" }}>
                      <Button>
                        <DeleteOutlined
                          onClick={() => {
                            const deletedItem =
                              formState.configuration?.customFields?.find(
                                (f) => f.id === field.id
                              );
                            if (deletedItem) {
                              deletedItem.flag = "delete";
                              setValue("enableSaveButton", true);
                              setUpdatedFieldsFlag("FieldDeactivated");
                            }
                          }}
                        />
                      </Button>
                    </Grid2>
                  </Grid2>
                </AccordionSummary>
                <AccordionDetails>
                  <CustomizableFormField
                    field={field}
                    onItemUpdate={(field) => {
                      const newCustomFields = [
                        ...(formState.configuration?.customFields ?? []).filter(
                          (f) => f.id !== field.id
                        ),
                        field,
                      ];
                      setValue("configuration.customFields", newCustomFields);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;

                      checkField({
                        iterableFieldName: `customFields.{idx}.label`,
                        idx: index,
                        value: field.label,
                        errors: newErrors,
                        tabContainingErrors: VIEW_NAMES[4],
                        setErrors: (errors) => {
                          setValue("validationErrors", errors);
                        },
                      });

                      if (field.type === "Select") {
                        field.options?.forEach((option, optionIndex) => {
                          checkField({
                            iterableFieldName: `customFields.{idx}.options.{idx}.name`,
                            idx: [index, optionIndex],
                            value: option.name,
                            errors: newErrors,
                            tabContainingErrors: VIEW_NAMES[4],
                            setErrors: (errors) => {
                              setValue("validationErrors", errors);
                            },
                          });
                        });
                      }

                      if (formState.enableSaveButton !== true) {
                        setValue("enableSaveButton", true);
                      }
                    }}
                    validationErrors={formState.validationErrors}
                    index={index}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Paper>
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 500,
          textAlign: "left",

          width: "100%",
        }}
      >
        {getLocalizedMessage(language, "reportingData.itemLevel") ||
          `Reporting Data on ${itemName.single} Level`}
      </Typography>

      <Paper
        sx={{
          borderRadius: "12px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        <Grid2
          size={12}
          container
          alignItems="center"
          sx={{
            height: 65,
            p: 2,
            width: "98%",
          }}
        >
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataField") || "Data Field"}
          </Grid2>
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataType") || "Data Type"}
          </Grid2>
          <Grid2 size={3}></Grid2>
          <Grid2 size={1.8}></Grid2>
          <Grid2 size={1.2}>
            <Button
              variant="outlined"
              onClick={() => handleAppend("itemReporting")}
              startIcon={<Add />}
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                padding: "7px 16px",
                whiteSpace: "nowrap",
                minWidth: "fit-content",
              }}
            >
              {(
                getLocalizedMessage(language, "addItem") ||
                `Add ${itemName.single}`
              ).replace("{{itemName}}", itemName.single)}
            </Button>
          </Grid2>
        </Grid2>
        {formState.configuration?.fieldsDefinition
          ?.filter((f) => f.purpose === "itemReporting")
          ?.map((field) => {
            const isExpandable =
              field.type === "Dropdown" || !!field.fieldInConfiguration;
            return (
              <Accordion
                expanded={isAccordionExpanded === field.fieldName}
                onChange={handleChange(field.fieldName as string)}
                elevation={0}
                key={field.fieldName + field.purpose}
                disableGutters
              >
                <AccordionSummary
                  aria-controls="panel1-content"
                  id="panel1-header"
                  expandIcon={isExpandable ? <ExpandMoreIcon /> : null}
                  sx={{
                    cursor: "default",
                    userSelect: "text",
                    width: isExpandable ? "100%" : "98%",
                    height: 65,
                  }}
                >
                  <Grid2
                    size={12}
                    container
                    alignItems="center"
                    sx={{ height: 65 }}
                  >
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {field.fieldName === "spentBudget"
                        ? `${field.label} (€)`
                        : field.label}
                    </Grid2>
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {field.type}
                    </Grid2>
                    <Grid2 size={3}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                        control={
                          <Checkbox
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                isMandatory: e.target.checked,
                              };

                              const newFieldsDefinition =
                                formState.configuration?.fieldsDefinition ?? [];
                              // Find the correct index in the full array
                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) =>
                                    f.fieldName === field.fieldName &&
                                    f.purpose === field.purpose
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  `configuration.fieldsDefinition`,
                                  newFieldsDefinition
                                );
                              }
                              if (formState.enableSaveButton !== true) {
                                setValue("enableSaveButton", true);
                              }
                            }}
                            disabled={field.isEditable === false}
                            icon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  border: "2px solid",
                                  borderColor:
                                    field.isEditable === false
                                      ? grey[400]
                                      : grey[700],

                                  display: "inline-block",
                                  backgroundColor: "#fff",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,

                                  border: "2px solid",
                                  borderColor:
                                    field.isEditable === false
                                      ? grey[400]
                                      : grey[700],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <CheckIcon
                                  style={{
                                    fontSize: 18,
                                    color:
                                      field.isEditable === false
                                        ? grey[400]
                                        : grey[700],
                                  }}
                                />
                              </span>
                            }
                            checked={field.isMandatory}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "mandatory") ||
                          "Mandatory"
                        }
                      />
                    </Grid2>
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {field.isEditable === false ? (
                        <Chip
                          label={
                            getLocalizedMessage(
                              language,
                              "constantlyActiveField"
                            ) || "Constantly Active Field"
                          }
                          sx={{
                            color: SUCCESS_COLORS.text,
                            fontWeight: 500,
                            backgroundColor: SUCCESS_COLORS.background,
                          }}
                        ></Chip>
                      ) : (
                        <FormControlLabel
                          control={
                            <AntSwitch
                              checked={field.enabled}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onChange={(e) => {
                                const currentField = {
                                  ...field,
                                  enabled: e.target.checked,
                                };

                                const newFieldsDefinition = [
                                  ...(formState.configuration
                                    ?.fieldsDefinition ?? []),
                                ];
                                // Find the correct index in the full array
                                const fullArrayIndex =
                                  newFieldsDefinition.findIndex(
                                    (f) =>
                                      f.fieldName === field.fieldName &&
                                      f.purpose === field.purpose
                                  );

                                if (fullArrayIndex !== -1) {
                                  newFieldsDefinition[fullArrayIndex] =
                                    currentField;

                                  setValue(
                                    `configuration.fieldsDefinition`,
                                    newFieldsDefinition
                                  );
                                }
                                if (formState.enableSaveButton !== true) {
                                  setValue("enableSaveButton", true);
                                }
                              }}
                            />
                          }
                          label={
                            getLocalizedMessage(language, "activate") ||
                            "Activate"
                          }
                          labelPlacement="start"
                          sx={{
                            "& .MuiSwitch-thumb": {
                              backgroundColor: grey[300],
                            },
                            "& .MuiSwitch-track": {
                              color: TOGGLE_GREEN,
                            },
                            "& .MuiFormControlLabel-label": {
                              fontSize: "14px",
                            },
                          }}
                        />
                      )}
                    </Grid2>
                  </Grid2>
                </AccordionSummary>
                {renderTheCorrectComponent(
                  field.fieldInConfiguration as
                    | "statusDefinition"
                    | "degreeOfCompletionDefinition"
                    | "semaphoreStatusDefinition",
                  setValue,
                  formState.configuration,
                  formState.validationErrors,
                  language,
                  field,
                  formState.enableSaveButton
                )}
              </Accordion>
            );
          })}
        {formState.configuration?.customFields
          ?.filter((f) => f.flag !== "delete")
          ?.map((field, index) => {
            if (field.purpose !== "itemReporting") {
              return null;
            }
            return (
              <Accordion
                expanded={isAccordionExpanded === field.id.toString()}
                onChange={handleChange(field.id.toString() as string)}
                elevation={0}
                key={field.id}
                disableGutters
              >
                <AccordionSummary
                  aria-controls="panel1-content"
                  id="panel1-header"
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      marginLeft: 0,
                    },
                    cursor: "default",
                    userSelect: "text",
                    width: "100%",
                    height: 65,
                  }}
                >
                  <Grid2
                    size={12}
                    container
                    alignItems="center"
                    sx={{ height: 65 }}
                  >
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {field.label}
                    </Grid2>
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {field.type}
                    </Grid2>
                    <Grid2 size={3}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                        control={
                          <Checkbox
                            onClick={(e) => {
                              // stop the click from bubbling to AccordionSummary
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                mandatory: e.target.checked,
                              };

                              const newFieldsDefinition =
                                formState.configuration?.customFields ?? [];
                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) => f.id === field.id
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  `configuration.customFields`,
                                  newFieldsDefinition
                                );
                              }
                            }}
                            icon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  border: "2px solid",
                                  borderColor: grey[700],

                                  display: "inline-block",
                                  backgroundColor: "#fff",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,

                                  border: "2px solid",
                                  borderColor: grey[700],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <CheckIcon
                                  style={{
                                    fontSize: 18,
                                    color: grey[700],
                                  }}
                                />
                              </span>
                            }
                            checked={field.mandatory}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "mandatory") ||
                          "Mandatory"
                        }
                      />
                    </Grid2>
                    <Grid2 size={2} sx={{ fontSize: "14px" }}>
                      <FormControlLabel
                        control={
                          <AntSwitch
                            checked={field.enabled}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                enabled: e.target.checked,
                              };

                              const newFieldsDefinition = [
                                ...(formState.configuration?.customFields ??
                                  []),
                              ];
                              // Find the correct index in the full array
                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) => f.id === field.id
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  `configuration.fieldsDefinition`,
                                  newFieldsDefinition
                                );
                              }
                            }}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "activate") ||
                          "Activate"
                        }
                        labelPlacement="start"
                        sx={{
                          "& .MuiSwitch-thumb": {
                            backgroundColor: grey[300],
                          },
                          "& .MuiSwitch-track": {
                            color: TOGGLE_GREEN,
                          },
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                      />
                    </Grid2>
                    <Grid2 size={1} sx={{ fontSize: "14px" }}>
                      <Button>
                        <DeleteOutlined
                          onClick={() => {
                            const deletedItem =
                              formState.configuration?.customFields?.find(
                                (f) => f.id === field.id
                              );
                            if (deletedItem) {
                              deletedItem.flag = "delete";
                              setValue("enableSaveButton", true);
                            }
                          }}
                        />
                      </Button>
                    </Grid2>
                  </Grid2>
                </AccordionSummary>
                <AccordionDetails>
                  <CustomizableFormField
                    field={field}
                    onItemUpdate={(field) => {
                      const newCustomFields = [
                        ...(formState.configuration?.customFields ?? []).filter(
                          (f) => f.id !== field.id
                        ),
                        field,
                      ];
                      setValue("configuration.customFields", newCustomFields);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;

                      checkField({
                        iterableFieldName: `customFields.{idx}.label`,
                        idx: index,
                        value: field.label,
                        errors: newErrors,
                        tabContainingErrors: VIEW_NAMES[4],
                        setErrors: (errors) => {
                          setValue("validationErrors", errors);
                        },
                      });

                      if (field.type === "Select") {
                        field.options?.forEach((option, optionIndex) => {
                          checkField({
                            iterableFieldName: `customFields.{idx}.options.{idx}.name`,
                            idx: [index, optionIndex],
                            value: option.name,
                            errors: newErrors,
                            tabContainingErrors: VIEW_NAMES[4],
                            setErrors: (errors) => {
                              setValue("validationErrors", errors);
                            },
                          });
                        });
                      }
                    }}
                    validationErrors={formState.validationErrors}
                    index={index}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Paper>

      {formState.configuration?.useCase === "distribution" && (
        <FormBlocksSection language={language} />
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 830px)",
          flexGrow: 1,
        }}
      ></Box>
    </Box>
  );
};

const renderTheCorrectComponent = (
  type:
    | "degreeOfCompletionDefinition"
    | "statusDefinition"
    | "semaphoreStatusDefinition"
    | "answerDefinition"
    | "distributionChannelDefinition"
    | "dcRelevanceDefinition"
    | undefined,

  setValue: (key: string, value: unknown) => void,
  configuration: Configuration | undefined,
  validationErrors: Record<string, ExtendedFieldError>,
  language: string,
  field: FieldDefinition & {
    purpose:
      "projectReporting" | "itemReporting" | "projectBasic" | "itemBasic";
  },
  enableSaveButton?: boolean
) => {
  switch (type) {
    case "statusDefinition":
      return (
        <AccordionDetails>
          <StatusFields
            onChange={(val) => {
              setValue(`configuration.statusDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              // remove existing statusDefinition.* errors
              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("statusDefinition.")) delete newErrors[k];
              });

              val.forEach((item, index) => {
                checkField({
                  iterableFieldName: `statusDefinition.{idx}.name`,
                  idx: index,
                  value: item.name,
                  errors: newErrors,
                  tabContainingErrors: VIEW_NAMES[4],
                  setErrors: (errors) => {
                    setValue("validationErrors", errors);
                  },
                });
              });

              setValue("validationErrors", newErrors);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            title="Define which status the tracking item can assume."
            initialEntries={configuration?.statusDefinition ?? []}
            type="statusDefinition"
            validationErrors={validationErrors}
          />
        </AccordionDetails>
      );
    case "answerDefinition":
      return (
        <AccordionDetails>
          <CategoryFields
            baseFieldName="answerDefinition"
            initialEntries={configuration?.answerDefinition ?? []}
            onEntriesChange={(val) => {
              setValue(`configuration.answerDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              val.forEach((item, index) => {
                checkField({
                  iterableFieldName: `answerDefinition.{idx}.name`,
                  idx: index,
                  value: item.name,
                  errors: newErrors,
                  tabContainingErrors: VIEW_NAMES[4],
                  setErrors: (errors) => {
                    setValue("validationErrors", errors);
                  },
                });
              });
            }}
            fieldName={field.label}
            onFieldNameChange={(name) => {
              const indexOfField =
                configuration?.fieldsDefinition?.findIndex(
                  (f) =>
                    f.fieldName === field.fieldName &&
                    f.purpose === field.purpose
                ) ?? 0;
              const newFieldsDefinition = configuration?.fieldsDefinition ?? [];
              newFieldsDefinition[indexOfField] = {
                ...newFieldsDefinition[indexOfField],
                label: name,
              };
              setValue(`configuration.fieldsDefinition`, newFieldsDefinition);
            }}
            validationErrors={validationErrors}
          />
        </AccordionDetails>
      );

    case "degreeOfCompletionDefinition":
      return (
        <AccordionDetails>
          <StatusFields
            enableColorPicker={true}
            onChange={(val) => {
              setValue(`configuration.degreeOfCompletionDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              val.forEach((item, index) => {
                checkField({
                  iterableFieldName: `degreeOfCompletionDefinition.{idx}.name`,
                  idx: index,
                  value: item.name,
                  errors: newErrors,
                  tabContainingErrors: VIEW_NAMES[4],
                  setErrors: (errors) => {
                    setValue("validationErrors", errors);
                  },
                });
              });

              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            title={
              getLocalizedMessage(language, "statusFields.statusTitle") ||
              "Define which status the tracking item can assume."
            }
            initialEntries={configuration?.degreeOfCompletionDefinition ?? []}
            type="degreeOfCompletionDefinition"
            validationErrors={validationErrors}
            language={language}
          />
        </AccordionDetails>
      );
    case "semaphoreStatusDefinition":
      return (
        <AccordionDetails>
          <SemaphoreFields
            initialEntries={configuration?.semaphoreStatusDefinition ?? []}
            initialReportingLevel={configuration?.statusReportingLevel}
            onEntriesChange={(data) => {
              setValue(`configuration.semaphoreStatusDefinition`, data.entries);
              setValue(`configuration.statusReportingLevel`, data.level);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              data.entries.forEach((item, index) => {
                checkField({
                  iterableFieldName: `semaphoreStatusDefinition.{idx}.name`,
                  idx: index,
                  value: item.name,
                  errors: newErrors,
                  tabContainingErrors: VIEW_NAMES[4],
                  setErrors: (errors) => {
                    setValue("validationErrors", errors);
                  },
                });
              });

              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            validationErrors={validationErrors}
            initialStatusEntries={configuration?.statusDefinition ?? []}
            onStatusEntriesChange={(val) => {
              setValue(`configuration.statusDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              // remove existing semaphoreStatusDefinition.* errors
              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("semaphoreStatusDefinition."))
                  delete newErrors[k];
              });

              val.forEach((item, index) => {
                if ((item.name ?? "").trim() === "") {
                  newErrors[`semaphoreStatusDefinition.${index}.name`] = {
                    type: "required",
                    message:
                      getLocalizedMessage(
                        language,
                        "validationErrors.semaphoreStatusNameRequired"
                      ) || "Semaphore status name cannot be empty",
                    tabContainingErrors: "Reporting",
                  } as ExtendedFieldError;
                }
              });

              setValue("validationErrors", newErrors);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
          ></SemaphoreFields>
        </AccordionDetails>
      );
    case "distributionChannelDefinition": {
      const channelLabels = (
        configuration?.distributionChannelDefinition ?? []
      ).map((c) => c.label);
      return (
        <AccordionDetails>
          <FixedEnumValuesPreview values={channelLabels} />
        </AccordionDetails>
      );
    }
    case "dcRelevanceDefinition": {
      const dcValues = configuration?.dcRelevanceDefinition ?? [];
      return (
        <AccordionDetails>
          <FixedEnumValuesPreview values={dcValues} />
        </AccordionDetails>
      );
    }
    default: {
      return (
        <AccordionDetails>
          <DefaultFields />
        </AccordionDetails>
      );
    }
  }
};
